import { cn } from '@immo/ui-kit';
import { useTranslations } from '@/i18n/use-translations';
import { SearchIcon } from '@/components/layout/icons';
import { Kbd } from './kbd';
import { shortcutLabel } from './platform';

type Variant = 'topbar' | 'overlay';

type Props = {
  readonly variant: Variant;
  // `topbar` ignores both — it is a trigger, not a field.
  readonly value?: string;
  readonly onValueChange?: (value: string) => void;
  readonly onClick?: () => void;
  readonly onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  // Combobox wiring for the overlay variant: the field keeps focus while ↑/↓
  // move the highlight, so the active row is announced through
  // `aria-activedescendant` rather than by moving focus onto it.
  readonly controlsId?: string;
  readonly activeDescendantId?: string | undefined;
};

/**
 * The search row — one component in two places. The topbar renders it as the
 * trigger that opens the palette; the overlay renders it as the real field. Same
 * parts, same order, same copy, so the field in the overlay reads as a
 * continuation of the one that was clicked rather than a second control.
 */
export function GlobalSearchEntry({
  variant,
  value,
  onValueChange,
  onClick,
  onKeyDown,
  controlsId,
  activeDescendantId,
}: Props) {
  const t = useTranslations('pages.shell.search');
  const isOverlay = variant === 'overlay';
  const shortcut = shortcutLabel();

  const parts = (
    <>
      <SearchIcon className={cn('shrink-0 text-text-tertiary', isOverlay ? 'h-5 w-5' : 'h-4 w-4')} aria-hidden />
      {isOverlay ? (
        <input
          type="text"
          role="combobox"
          aria-expanded
          aria-controls={controlsId}
          aria-activedescendant={activeDescendantId}
          aria-autocomplete="list"
          autoComplete="off"
          value={value ?? ''}
          onChange={(event) => onValueChange?.(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t('placeholder')}
          aria-label={t('placeholder')}
          className="min-w-0 flex-1 bg-transparent text-lg text-text-primary outline-none placeholder:text-text-tertiary"
        />
      ) : (
        <span className="hidden min-w-0 flex-1 truncate text-left text-sm text-text-tertiary xl:block">
          {t('placeholder')}
        </span>
      )}
      <Kbd className={cn(!isOverlay && 'hidden xl:inline-flex')}>{shortcut}</Kbd>
    </>
  );

  if (isOverlay) {
    return <div className="flex items-center gap-3 border-b border-border px-4 py-3">{parts}</div>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t('topbar-hint')}
      title={t('topbar-hint')}
      className="flex h-9 w-9 items-center justify-center gap-2 rounded-md border border-border bg-canvas px-0 shadow-sm transition-colors hover:bg-hover-bg focus:outline-none focus:shadow-focus xl:w-full xl:justify-start xl:px-3"
    >
      {parts}
    </button>
  );
}
